// Offline Support (120): status strip for the score pages.
//
// Shows three things, none hover-gated:
//   1. an offline indicator when the browser reports no connection,
//   2. a count of score writes queued locally and waiting to sync,
//   3. per-item rejection notices (server refused the write on replay) with
//      the local-only data so the coach can re-enter it, then dismiss.
//
// Driven entirely by useScoreQueue(matchId); render it near the top of each
// score page.

import { CloudOff, RefreshCw, AlertTriangle, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useScoreQueue } from "@/lib/offline/use-score-queue";

export function OfflineScoreBar({ matchId }: { matchId: string | undefined }) {
  const { online, pendingCount, rejected, dismissRejected, dismissItem } =
    useScoreQueue(matchId);

  const showStatus = !online || pendingCount > 0;
  if (!showStatus && rejected.length === 0) return null;

  return (
    <div className="space-y-2" data-offline-bar>
      {showStatus && (
        <div
          className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-offline-status
          data-online={online ? "true" : "false"}
        >
          {online ? (
            <RefreshCw className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <CloudOff className="h-4 w-4 shrink-0" aria-hidden />
          )}
          <span className="flex-1">
            {online
              ? "Reconnected -- syncing your score changes."
              : "You're offline. Score changes are saved on this device and will sync when you reconnect."}
          </span>
          {pendingCount > 0 && (
            <Badge variant="secondary" data-offline-pending-count>
              {pendingCount} queued
            </Badge>
          )}
        </div>
      )}

      {rejected.length > 0 && (
        <div
          className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm"
          data-offline-rejected
        >
          <div className="mb-1 flex items-center gap-2 font-medium text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            <span className="flex-1">
              {rejected.length} queued change{rejected.length === 1 ? "" : "s"} could
              not be applied
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => void dismissRejected()}
            >
              Dismiss all
            </Button>
          </div>
          <p className="mb-2 text-xs text-muted-foreground">
            The match state changed before these synced (for example it was
            finalised). Re-enter anything still needed.
          </p>
          <ul className="space-y-1">
            {rejected.map((item) => (
              <li
                key={item.client_event_id}
                className="flex items-center justify-between gap-2 rounded border border-border bg-background px-2 py-1"
                data-offline-rejected-item
              >
                <span className="truncate">
                  <span className="font-medium">{item.label}</span>
                  {item.rejection?.message ? (
                    <span className="text-muted-foreground">
                      {" "}
                      -- {item.rejection.message}
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Dismiss"
                  onClick={() => void dismissItem(item.client_event_id)}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
